/** Page des bornes - liste avec statut, création, suppression */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { KeyRound, Pencil, Plus, ShieldOff, Smartphone, Trash2 } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Badge } from '../components/ui/Badge';
import { Table, type Column } from '../components/ui/Table';
import {
  useReaders,
  useCreateReader,
  useUpdateReader,
  useDeleteReader,
  useCreatePairingCode,
  useRevokeDevice,
} from '../hooks/useReaders';
import { useSites } from '../hooks/useSites';
import { formatDate, formatTime } from '../lib/utils';
import type { Reader } from '../types';

const createSchema = z.object({
  code: z.string().min(1, 'Code requis'),
  name: z.string().min(1, 'Nom requis'),
  site_id: z.coerce.number().min(1, 'Site requis'),
  location: z.string().optional(),
});

type CreateFormData = z.infer<typeof createSchema>;

export function ReadersPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Reader | null>(null);

  const { data: readers, isLoading } = useReaders();
  const createMutation = useCreateReader();
  const updateMutation = useUpdateReader();
  const deleteMutation = useDeleteReader();
  const pairMutation = useCreatePairingCode();
  const revokeMutation = useRevokeDevice();

  // Modal PIN d'appairage
  const [pairing, setPairing] = useState<{
    reader: Reader;
    pin: string;
    expiresAt: string;
  } | null>(null);

  const handlePair = async (reader: Reader) => {
    const res = await pairMutation.mutateAsync(reader.id);
    setPairing({ reader, pin: res.pin, expiresAt: res.expires_at });
  };

  const handleRevoke = (reader: Reader) => {
    if (!window.confirm(`Révoquer l'appairage de "${reader.name}" ? La tablette devra être ré-appairée avec un nouveau PIN.`)) return;
    revokeMutation.mutate(reader.id);
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateFormData>({
    resolver: zodResolver(createSchema),
    defaultValues: { site_id: 1 },
  });
  const { data: sites } = useSites();

  const openCreate = () => {
    setEditing(null);
    reset({ code: '', name: '', site_id: sites?.[0]?.id ?? 1, location: '' });
    setModalOpen(true);
  };

  const openEdit = (reader: Reader) => {
    setEditing(reader);
    reset({
      code: reader.code,
      name: reader.name,
      site_id: reader.site_id,
      location: reader.location ?? '',
    });
    setModalOpen(true);
  };

  const onSubmit = async (data: CreateFormData) => {
    if (editing) {
      await updateMutation.mutateAsync({
        id: editing.id,
        data: {
          code: data.code,
          name: data.name,
          site_id: data.site_id,
          location: data.location || undefined,
        },
      });
    } else {
      await createMutation.mutateAsync({
        code: data.code,
        name: data.name,
        site_id: data.site_id,
        location: data.location || undefined,
      });
    }
    setModalOpen(false);
    setEditing(null);
    reset();
  };

  const handleDelete = (id: number, name: string) => {
    if (window.confirm(`Désactiver la borne "${name}" ?`)) {
      deleteMutation.mutate(id);
    }
  };

  const columns: Column<Reader>[] = [
    {
      key: 'code',
      header: 'Code',
      render: (r) => <span className="font-mono text-sm font-medium">{r.code}</span>,
    },
    { key: 'name', header: 'Nom' },
    {
      key: 'site_name',
      header: 'Site',
      render: (r) => r.site_name || '—',
    },
    {
      key: 'location',
      header: 'Emplacement',
      render: (r) => r.location || '—',
    },
    {
      key: 'status',
      header: 'État',
      render: (r) => (
        <Badge variant={r.status === 'online' ? 'success' : 'danger'}>
          {r.status === 'online' ? 'En ligne' : 'Hors ligne'}
        </Badge>
      ),
    },
    {
      key: 'paired',
      header: 'Appairage',
      render: (r) =>
        r.paired ? (
          <Badge variant="info">
            <Smartphone size={10} className="mr-1" /> Appairée
          </Badge>
        ) : (
          <Badge variant="warning">Non appairée</Badge>
        ),
    },
    {
      key: 'last_punch',
      header: 'Dernier pointage',
      render: (r) =>
        r.last_punch ? (
          <span className="text-xs text-gray-500">
            {formatDate(r.last_punch)} {formatTime(r.last_punch)}
          </span>
        ) : (
          <span className="text-xs text-gray-400">Aucun</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="flex items-center gap-1 justify-end">
          {r.paired ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleRevoke(r)}
              title="Révoquer l'appairage"
            >
              <ShieldOff size={13} className="text-amber-600" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePair(r)}
              loading={pairMutation.isPending}
              title="Générer un PIN d'appairage"
            >
              <KeyRound size={13} className="text-accent" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEdit(r)}
            title="Modifier la borne"
          >
            <Pencil size={13} className="text-stone-500" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(r.id, r.name)}
            title="Désactiver la borne"
          >
            <Trash2 size={13} className="text-red-500" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Header
        title="Bornes"
        action={
          <Button size="sm" onClick={openCreate}>
            <Plus size={14} className="mr-1" /> Ajouter
          </Button>
        }
      />

      {/* Vue tableau ≥ md */}
      <div className="hidden md:block">
        <Card>
          <Table
            columns={columns}
            data={readers ?? []}
            loading={isLoading}
            emptyMessage="Aucune borne configurée"
          />
        </Card>
      </div>

      {/* Vue cartes en mobile */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          <Card><p className="text-sm text-stone-400">Chargement...</p></Card>
        ) : !readers?.length ? (
          <Card><p className="text-sm text-stone-400">Aucune borne configurée</p></Card>
        ) : (
          readers.map((r) => (
            <div
              key={r.id}
              className="bg-white rounded-xl border border-stone-200 shadow-soft p-4"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0">
                  <p className="font-semibold text-ink truncate">{r.name}</p>
                  <p className="font-mono text-xs text-stone-500 mt-0.5">{r.code}</p>
                </div>
                <div className="flex items-center gap-1 -mr-2">
                  {r.paired ? (
                    <button
                      onClick={() => handleRevoke(r)}
                      className="p-2 rounded-lg hover:bg-stone-100"
                      title="Révoquer l'appairage"
                    >
                      <ShieldOff size={15} className="text-amber-600" />
                    </button>
                  ) : (
                    <button
                      onClick={() => handlePair(r)}
                      className="p-2 rounded-lg hover:bg-stone-100"
                      title="Générer un PIN"
                    >
                      <KeyRound size={15} className="text-accent" />
                    </button>
                  )}
                  <button
                    onClick={() => openEdit(r)}
                    className="p-2 rounded-lg hover:bg-stone-100"
                    title="Modifier"
                  >
                    <Pencil size={15} className="text-stone-500" />
                  </button>
                  <button
                    onClick={() => handleDelete(r.id, r.name)}
                    className="p-2 rounded-lg hover:bg-rose-50"
                    title="Désactiver"
                  >
                    <Trash2 size={15} className="text-red-500" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div>
                  <p className="text-stone-400 uppercase tracking-wide font-medium text-[10px]">Site</p>
                  <p className="text-ink mt-0.5">{r.site_name || '—'}</p>
                </div>
                <div>
                  <p className="text-stone-400 uppercase tracking-wide font-medium text-[10px]">Emplacement</p>
                  <p className="text-ink mt-0.5">{r.location || '—'}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-stone-100">
                <Badge variant={r.status === 'online' ? 'success' : 'danger'}>
                  {r.status === 'online' ? 'En ligne' : 'Hors ligne'}
                </Badge>
                {r.paired ? (
                  <Badge variant="info">
                    <Smartphone size={10} className="mr-1" /> Appairée
                  </Badge>
                ) : (
                  <Badge variant="warning">Non appairée</Badge>
                )}
                {r.last_punch && (
                  <span className="text-[11px] text-stone-500 ml-auto">
                    Dernier : {formatDate(r.last_punch)} {formatTime(r.last_punch)}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal PIN d'appairage */}
      <Modal
        open={!!pairing}
        onClose={() => setPairing(null)}
        title="PIN d'appairage tablette"
        footer={
          <Button onClick={() => setPairing(null)}>J'ai noté le PIN</Button>
        }
      >
        {pairing && (
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              Saisis ce code sur la tablette <strong>{pairing.reader.name}</strong> (code{' '}
              <code className="font-mono">{pairing.reader.code}</code>) dans les 10 minutes :
            </div>
            <div className="flex justify-center py-4">
              <div className="text-5xl font-bold num tracking-[0.3em] text-accent bg-accent-soft px-6 py-4 rounded-lg border border-accent/30">
                {pairing.pin}
              </div>
            </div>
            <div className="text-xs text-gray-500 text-center">
              ⏱ Expire le{' '}
              {new Date(pairing.expiresAt).toLocaleTimeString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit',
              })}
              . Usage unique — après appairage, le PIN est consommé.
            </div>
          </div>
        )}
      </Modal>

      {/* Modal création / édition */}
      <Modal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); reset(); }}
        title={editing ? `Modifier la borne ${editing.code}` : 'Nouvelle borne'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {editing ? 'Enregistrer' : 'Créer'}
            </Button>
          </>
        }
      >
        <form className="space-y-3">
          <Input
            label="Code"
            {...register('code')}
            error={errors.code?.message}
            placeholder="ARG_ENTREE_3"
          />
          <Input
            label="Nom"
            {...register('name')}
            error={errors.name?.message}
            placeholder="Entrée secondaire"
          />
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-600">Site</label>
            <select
              {...register('site_id')}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm
                         focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
            >
              {sites?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="Emplacement (optionnel)"
            {...register('location')}
            placeholder="Hall d'entrée"
          />
        </form>
      </Modal>
    </div>
  );
}
